/**
 * The item map: which QuickBooks product each rate band posts to.
 *
 * QuickBooks refuses an invoice line without a Product/Service, and it
 * has never heard of Olive's rate bands. This is the phrasebook, and
 * until it had a screen the only way to write in it was a database
 * console.
 *
 * The list is NOT the rate-band vocabulary. The push looks up whatever
 * token is on the invoice line, and the derivation writes
 * COALESCE(rate_band, rate_label), so a legacy line carries a raw label
 * like "24 - Care Management". A screen built from the vocabulary would
 * let an operator map every row it shows while the invoice that is
 * actually failing names a token it never lists. The server sends the
 * union of what is mapped and what is on an unpushed invoice; this
 * renders what it is sent.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { qboApi, type QboItemMapRowWire } from '@/lib/qboApi';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** One sentence per refusal, naming the next action. The wire token is
 *  never rendered: it is a fact for the log, not for an operator. */
const REFUSAL_COPY: Readonly<Record<string, string>> = {
  not_connected: 'Connect QuickBooks before mapping products.',
  multiple_connections:
    'Two QuickBooks companies are connected. Disconnect one before mapping products.',
  realm_mismatch:
    'This map was built for a different QuickBooks company. Start it over to map against the connected one.',
  item_not_in_realm: 'QuickBooks has no product with that number. Check the number in QuickBooks, then enter it again.',
};

/** The 'unavailable' surface: the server could not find out whether the
 *  id is real, so nothing was written. Every sentence here says NOT
 *  SAVED out loud, because the field still holds the draft and silence
 *  would read as success. item_read_failed lived in REFUSAL_COPY once,
 *  where it was unreachable: the server emits it as an 'unavailable',
 *  and this component only rendered copy for 'refused'. */
const UNAVAILABLE_COPY: Readonly<Record<string, string>> = {
  item_read_failed:
    "Your QuickBooks products couldn't be read, so this wasn't saved. Try again in a few minutes, and if it fails again, tell {contact_billing}.",
};

/** The token_* family (resolveUsableAccess) and anything unruled: one
 *  honest sentence, never the wire token. */
const UNAVAILABLE_FALLBACK =
  "QuickBooks can't be reached, so this wasn't saved. Press Reconnect QuickBooks, then save again.";

/** A thrown transport error (the request itself failed): the same
 *  not-saved posture, without claiming to know why. */
const SAVE_FAILED_SENTENCE =
  "This product choice didn't save. Try again in a few minutes, and if it fails again, tell {contact_system}.";

const RESET_FAILED_SENTENCE =
  "The product choices weren't cleared. Try again in a few minutes, and if it fails again, tell {contact_system}.";

function refusalSentence(reason: string | undefined, contact: SupportContact): string {
  return contactSentence(
    (reason !== undefined && REFUSAL_COPY[reason]) ||
      "That didn't go through, and nothing was saved. Try again, and if it happens again, tell {contact_system}.",
    contact,
  );
}

function unavailableSentence(reason: string | undefined, contact: SupportContact): string {
  return contactSentence((reason !== undefined && UNAVAILABLE_COPY[reason]) || UNAVAILABLE_FALLBACK, contact);
}

export function QboItemMapSection({ slug }: { slug: string }): React.ReactElement {
  const qc = useQueryClient();
  const contact = useSupportContact();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saidNo, setSaidNo] = useState<{ band: string; sentence: string } | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const mapQuery = useQuery({
    queryKey: ['qbo', 'item-map', slug],
    queryFn: ({ signal }) => qboApi.itemMap(slug, signal),
    enabled: open,
  });

  const saveMut = useMutation({
    mutationFn: (row: { band: string; qbo_item_id: string }) => qboApi.mapItem(slug, row),
    onSuccess: (result, row) => {
      if (result.outcome === 'refused') {
        setSaidNo({ band: row.band, sentence: refusalSentence(result.reason, contact) });
        return;
      }
      // 'unavailable' is NOT success: the server could not verify the
      // id, so nothing was written. The draft stays (there is nothing
      // to retype), the sentence says not-saved, and no refetch fires
      // (the map on the server is unchanged). Clearing the draft here
      // was the defect: the typed id vanished with zero feedback.
      if (result.outcome === 'unavailable') {
        setSaidNo({ band: row.band, sentence: unavailableSentence(result.reason, contact) });
        return;
      }
      setSaidNo(null);
      setDrafts((d) => {
        const next = { ...d };
        delete next[row.band];
        return next;
      });
      void qc.invalidateQueries({ queryKey: ['qbo', 'item-map', slug] });
    },
    // A thrown ApiError (500, dead socket) is the same not-saved
    // posture. Without this arm the button just went idle again.
    onError: (_e, row) => {
      setSaidNo({ band: row.band, sentence: contactSentence(SAVE_FAILED_SENTENCE, contact) });
    },
  });

  const [resetFailed, setResetFailed] = useState(false);
  const resetMut = useMutation({
    mutationFn: () => qboApi.clearItemMap(slug),
    onSuccess: (result) => {
      setConfirmingReset(false);
      // Only 'cleared' is a reset that happened (D4-9's server half):
      // the handler answers a failed write as a fact-shaped 200
      // 'unavailable' (map_clear_failed) with NOTHING changed, so a
      // status-only success arm would render a full map as freshly
      // cleared. Same not-cleared sentence as a thrown transport error.
      if (result.outcome !== 'cleared') {
        setResetFailed(true);
        return;
      }
      setResetFailed(false);
      setDrafts({});
      setSaidNo(null);
      void qc.invalidateQueries({ queryKey: ['qbo', 'item-map', slug] });
    },
    // A failed reset says so (D4-9): the dialog closes on the answer
    // either way, and the sentence below the action reports the truth.
    onError: () => {
      setConfirmingReset(false);
      setResetFailed(true);
    },
  });

  const data = mapQuery.data;
  const valueFor = (row: QboItemMapRowWire): string =>
    drafts[row.band] ?? row.qbo_item_id ?? '';

  return (
    <>
      <TextAction data-testid="qbo-item-map-open" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide product mapping' : 'Map rate bands to QuickBooks products'}
      </TextAction>

      {open && (
        <div className="pt-2" data-testid="qbo-item-map">
          {mapQuery.isPending && <p className="text-sm text-ink-soft">Reading the map…</p>}

          {data !== undefined && (
            <>
              {/* The connection states that stop a save, said before the
                  operator types into a field that cannot be saved. */}
              {data.connection.status !== 'connected' && (
                <p role="alert" data-testid="qbo-item-map-blocked" className="text-sm text-pepper">
                  {data.connection.status === 'multiple'
                    ? REFUSAL_COPY.multiple_connections
                    : data.connection.status === 'expired'
                      ? 'The QuickBooks connection needs renewing before you can pick products. Press Reconnect QuickBooks above.'
                      : REFUSAL_COPY.not_connected}
                </p>
              )}

              {data.bands.length === 0 ? (
                <p className="text-sm text-ink-soft">
                  Once you invoice work, you'll see its rate bands here.
                </p>
              ) : (
                <table data-testid="qbo-item-map-table" className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-ink-soft">
                      <th className="py-1 pr-3 font-medium">Rate band</th>
                      <th className="py-1 pr-3 font-medium">QuickBooks product number</th>
                      <th className="py-1 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.bands.map((row) => (
                      <tr
                        key={row.band}
                        data-testid={`qbo-item-map-row-${row.band}`}
                        className="border-t border-sage-100 align-top"
                      >
                        <td className="py-2 pr-3">
                          <span className="block">{row.label}</span>
                          {/* The urgent half: this token is on an issued
                              invoice and the push fails closed without it. */}
                          {row.on_unpushed_invoice && row.qbo_item_id === null && (
                            <span
                              data-testid={`qbo-item-map-waiting-${row.band}`}
                              className="text-xs text-pepper"
                            >
                              An invoice is waiting on this one.
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            data-testid={`qbo-item-map-input-${row.band}`}
                            aria-label={`QuickBooks product number for ${row.label}`}
                            className="w-40 rounded border border-sage-200 px-2 py-1"
                            value={valueFor(row)}
                            onChange={(e) =>
                              setDrafts((d) => ({ ...d, [row.band]: e.target.value }))
                            }
                          />
                          {saidNo?.band === row.band && (
                            <span
                              role="alert"
                              data-testid={`qbo-item-map-refused-${row.band}`}
                              className="mt-1 block text-xs text-pepper"
                            >
                              {saidNo.sentence}
                            </span>
                          )}
                        </td>
                        <td className="py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`qbo-item-map-save-${row.band}`}
                            disabled={
                              saveMut.isPending ||
                              valueFor(row).trim() === '' ||
                              valueFor(row).trim() === (row.qbo_item_id ?? '')
                            }
                            onClick={() =>
                              saveMut.mutate({ band: row.band, qbo_item_id: valueFor(row).trim() })
                            }
                          >
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {data.bound_realm_id !== null && (
                <div className="pt-3">
                  <TextAction
                    data-testid="qbo-item-map-reset-open"
                    onClick={() => setConfirmingReset(true)}
                  >
                    Start the map over
                  </TextAction>
                  {resetFailed && (
                    <p
                      role="alert"
                      data-testid="qbo-item-map-reset-failed"
                      className="mt-1 text-xs text-pepper"
                    >
                      {contactSentence(RESET_FAILED_SENTENCE, contact)}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {confirmingReset && (
        <ConfirmDialog
          destructive
          title="Start the product map over?"
          // Says what it does, not what it is called. The ids belong to
          // the company the map was built against, so a new company
          // cannot reuse them and there is no way to keep them.
          body="Every band's mapping is cleared and the QuickBooks company is unlinked, so you will need to map each band again."
          confirmLabel="Clear the map"
          onCancel={() => setConfirmingReset(false)}
          onConfirm={() => resetMut.mutate()}
        />
      )}
    </>
  );
}
